import React from 'react'
import { css } from '@emotion/react'
import { getMonth, getYear } from 'date-fns'
import ja from 'date-fns/locale/ja'
import { range } from 'lodash'
import { colors, font } from 'plume-ui'
import ReactDatepicker from 'react-datepicker'
import { IcomoonIcon } from '../IcomoonIcon'

type Props = {
  onChange: (date: Date) => void
  onDelete?: (() => void) | undefined
  selected?: Date | null
  className?: string
  maxDate?: Date | null | undefined
  minDate?: Date | null | undefined
}

export const DatePicker: React.FC<Props> = (props) => {
  const onDelete = props.onDelete
  return (
    <div
      className={props.className}
      css={css`
        box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
        > div {
          ${props.className?.includes('first-interview-calender') &&
          ' background: white;'}
        }
        // Override react-datepicker's style
        .react-datepicker {
          font-family: ${font.family};
          border: none;
        }

        .react-datepicker__month-container {
          padding: 10px 0px 5px;
        }

        .react-datepicker__header {
          background-color: ${colors.white00};
          border-bottom: none;
          margin-bottom: -8px;
        }

        .react-datepicker__day-name,
        .react-datepicker__day,
        .react-datepicker__time-name {
          line-height: 28px;
          width: 28px;
          margin: 5px;
        }

        .react-datepicker__day {
          border-radius: 50%;
          color: ${colors.text};
          transition: background-color 0.3s;

          &:hover {
            background-color: ${colors.gray40};
          }
          &:focus {
            outline: none;
          }
        }

        .react-datepicker__day--outside-month {
          color: ${colors.gray60};
        }

        .react-datepicker__day--selected {
          border-radius: 50%;
          background-color: ${colors.cyan60};
          color: ${colors.white00};
          &:hover {
            background-color: ${colors.cyan60};
          }
        }

        div.react-datepicker__day--today {
          font-weight: ${font.weight.normal};
          border: 1px solid ${colors.cyan60} !important;
          width: 30px;
        }

        .react-datepicker__day--today.react-datepicker__day--selected {
          color: ${colors.white00} !important;
        }

        .react-datepicker__day-name {
          color: ${colors.textLight};
          font-size: 12px;
        }

        .react-datepicker__day--disabled {
          color: ${colors.gray60};
          &:hover {
            background-color: ${colors.white00};
          }
        }
      `}
    >
      <ReactDatepicker
        dateFormat="yyyy-MM-dd"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        inline
        disabledKeyboardNavigation
        locale={ja}
        selected={props.selected}
        maxDate={props.maxDate}
        minDate={props.minDate}
        onChange={props.onChange}
        renderCustomHeader={(args) => (
          <CustomHeader
            {...args}
            minDate={props.minDate}
            maxDate={props.maxDate}
          />
        )}
      />
      {onDelete && (
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
          `}
        >
          <div
            className="clear-date-button"
            css={css`
              color: ${colors.link};
              margin-right: 16px;
              margin-bottom: 16px;
              font-size: 12px;
              cursor: pointer;
              :hover {
                text-decoration: underline;
              }
            `}
            onClick={() => onDelete()}
          >
            選択内容をクリア
          </div>
        </div>
      )}
    </div>
  )
}

const CustomHeader: React.FC<{
  date: Date
  changeYear: (year: number) => void
  changeMonth: (month: number) => void
  decreaseMonth: () => void
  increaseMonth: () => void
  prevMonthButtonDisabled: boolean
  nextMonthButtonDisabled: boolean
  minDate?: Date | null | undefined
  maxDate?: Date | null | undefined
}> = (props) => {
  const minYear = props.minDate
    ? getYear(props.minDate)
    : getYear(new Date()) - 30
  const maxYear = props.maxDate
    ? getYear(props.maxDate)
    : getYear(new Date()) + 10
  const years: number[] = range(minYear, maxYear + 1)
  let months: number[] = range(1, 13)

  const handlePrevMonthClick = () => {
    if (!props.prevMonthButtonDisabled) {
      props.decreaseMonth()
    }
  }

  const handleNextMonthClick = () => {
    if (!props.nextMonthButtonDisabled) {
      props.increaseMonth()
    }
  }

  const handleChangeYear = (year: number) => {
    if (props.maxDate && year === getYear(props.maxDate)) {
      // 今日の日付が渡された場合、表示する月を今月に制限する
      months = range(1, getMonth(props.maxDate) + 2)
    } else {
      months = range(1, 13)
    }
    props.changeYear(year)
  }

  const handleChangeMonth = (month: number) => {
    if (
      props.maxDate &&
      getYear(props.date) === getYear(props.maxDate) &&
      month > getMonth(props.maxDate)
    ) {
      return // 今日の日付が渡された場合、maxDateの月を超える月の選択は許可しない
    }
    props.changeMonth(months.indexOf(month))
  }

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 5px 10px;
      `}
    >
      <div
        onClick={handlePrevMonthClick}
        css={css`
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${props.prevMonthButtonDisabled
            ? colors.gray60
            : colors.black10};
          transition: background-color 0.3s;

          ${!props.prevMonthButtonDisabled &&
          `
            &:hover {
              cursor: pointer;
              background-color: ${colors.gray40};
          `}
        `}
      >
        <IcomoonIcon name="arrow4-left" />
      </div>
      <div>
        <Select
          value={getYear(props.date)}
          onChange={({ target }) => handleChangeYear(parseInt(target.value))}
          css={css`
            width: 85px;
            margin-right: 3px;
          `}
        >
          {years.map((option) => (
            <option key={option} value={option}>
              {`${option}年`}
            </option>
          ))}
        </Select>
        <Select
          value={months[getMonth(props.date)]}
          onChange={({ target }) => {
            handleChangeMonth(parseInt(target.value))
          }}
          css={css`
            width: 65px;
          `}
        >
          {months.map((option) => (
            <option key={option} value={option}>
              {`${option}月`}
            </option>
          ))}
        </Select>
      </div>
      <div
        className="next-month-button"
        onClick={handleNextMonthClick}
        css={css`
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: ${props.nextMonthButtonDisabled
            ? colors.gray60
            : colors.black10};
          transition: background-color 0.3s;

          ${!props.nextMonthButtonDisabled &&
          `
            &:hover {
              cursor: pointer;
              background-color: ${colors.gray40};
          `}
        `}
      >
        <IcomoonIcon name="arrow4-right" />
      </div>
    </div>
  )
}

const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (
  props
) => {
  return (
    <select
      {...props}
      className={props.className}
      css={css`
        border: none;
        font-size: 14px;
        padding: 5px 6px;
        border-radius: 2px;
        transition: background-color 0.3s;

        &:focus {
          outline: none;
        }
        &:hover {
          cursor: pointer;
          background-color: ${colors.gray40};
        }
      `}
    />
  )
}
